import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Button from './Button';
import Popup from './Popup';
import PopupContent from './PopupContent';
import CloseButton from './CloseButton';
import FaQuestionCircle from 'react-icons/lib/fa/question-circle';

/**
 * FieldMenu is a Menu that can be "open" or not.
 * When closed it appears to be a button. When open, it
 * utilizes a portal to display a div beside it with
 * clickable field-labeled buttons in a style similar to a table.
 *
 * @reactProps {string} title - the label for the button
 * @reactProps {Object} menuData - an object with menu categories, subcategories,
 *                                   names of products and file names
 * @reactProps {function} handleMenuToggle - takes a string (props.title)
 *                                              tells parent the menu was clicked
 * @reactProps {boolean} open - controls the style of the open and closed state
 * @reactProps {function} handleCloseButton - from parent container. ensures
 *                                              close button closes the menu
 * @reactProps {function} handleFieldChange - from Main, update the product selection
 */
export default class FieldMenu extends React.Component {
  /**
   * stores the rectangle for coordinates
   */
  state = {};

  /**
   * Tell parent component that this FieldMenu was toggled.
   *
   */
  handleMenuToggle = () => {
    this.props.handleMenuToggle(this.props.title);
  };

  /**
   * bounding rectangle for portal established
   */
  componentDidMount() {
    this.getStyle();
    window.addEventListener('resize', () => this.getStyle());
    document.addEventListener('scroll', () => this.getStyle());
  }

  /**
   * Set the rectangle object to state. The open menu div
   * has to be positioned relative to this rectangle.
   */
  getStyle = () => {
    const parent = document.getElementById(this.props.title);
    const rect = parent.getBoundingClientRect();
    this.setState({ parent, rect });
  };

  /**
   * When closed, renders a button displaying props.title
   *  Style determined with props.open, deconstructed and
   *  controlling the "open" className via cx.
   * When open, renders the button displaying props.title
   *  and a Popup > div > PopupContent.
   *  PopupContent children is an array of buttons.
   * Rendered components not yet listed are CloseButton and FaQuestionCircle.
   * @return button, div > Popup > div > PopupContent
   */
  render() {
    const { open } = this.props;
    let i = 0;
    let products = [];
    let handleFieldChange = this.props.handleFieldChange;

    if (this.props.menuData != null) {
      let data = this.props.menuData;
      products.push(
        <CloseButton handleCloseButton={this.props.handleCloseButton} />
      );
      Object.keys(data).forEach(category => {
        products.push(
          <div className="category">
            <div>
              <span />
              {category}
              <a
                className="help-icon"
                href={`../help/${data[category].help}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaQuestionCircle />
              </a>
            </div>
          </div>
        );
        Object.keys(data[category]).forEach(subcategory => {
          let buttons = [];
          Object.keys(data[category][subcategory]).forEach(product => {
            let value = data[category][subcategory][product]['productFile'];
            let unit = data[category][subcategory][product]['units'];
            buttons.push(
              <Button
                key={i++}
                label={product}
                value={value}
                onEvent={handleFieldChange}
                unit={unit}
                onClick={true}
              />
            );
          });
          if (subcategory !== 'help') {
            products.push(
              <div className="trow">
                <span className="subcat">{subcategory}:</span>
                {buttons}
              </div>
            );
          }
        });
      });
    }
    return (
      <button
        className={cx('closed-menu', { open })}
        id={this.props.title}
        onClick={this.handleMenuToggle}
      >
        <h3>{this.props.title}</h3>
        {open && (
          <Popup>
            <div className={cx('field-menu', 'opened')}>
              <PopupContent
                top={this.state.rect.top}
                left={this.state.rect.right + 5}
              >
                {' '}
                {products}{' '}
              </PopupContent>
            </div>
          </Popup>
        )}
      </button>
    );
  }
}

FieldMenu.propTypes = {
  title: PropTypes.string,
  menuItems: PropTypes.object,
  handleFieldChange: PropTypes.func,
  handleCloseButton: PropTypes.func,
  handleDateMenuToggle: PropTypes.funcs,
  open: PropTypes.bool
};



// WEBPACK FOOTER //
// ./src/FieldMenu.js